"use client";
import React from "react";

const RichTextRenderer = ({
    content,
    className = "",
    tag = "div",
    style = {},
    forceFont = false,
    disableParagraph = false,
}) => {
    const renderNode = (node, index) => {
        if (!node) return null;
        if (typeof node === "string") return node;

        // ── Text node ──────────────────────────────────────────
        if (node.type === "text") {
            const isBold         = node.bold         || node.marks?.some((m) => m.type === "bold");
            const isItalic       = node.italic        || node.marks?.some((m) => m.type === "italic");
            const isUnderlined   = node.underline     || node.marks?.some((m) => m.type === "underline");
            const isStrikethrough= node.strikethrough || node.deleted || node.marks?.some((m) => ["strikethrough", "deleted"].includes(m.type));
            const isCode         = node.code          || node.marks?.some((m) => m.type === "code");

            // Preserve whitespace — convert \n inside text to <br/>
            const text = node.text || "";
            const parts = text.split("\n");

            const inner = parts.map((part, i) => (
                <React.Fragment key={i}>
                    {part}
                    {i < parts.length - 1 && <br />}
                </React.Fragment>
            ));

            return (
                <span
                    key={index}
                    style={{
                        fontWeight:     isBold          ? "bold"         : undefined,
                        fontStyle:      isItalic        ? "italic"       : undefined,
                        textDecoration: isStrikethrough ? "line-through" : isUnderlined ? "underline" : undefined,
                        ...style,
                    }}
                    className={[
                        isBold || forceFont ? "font-saansBold" : "",
                        isCode ? "font-mono bg-slate-100 px-1 rounded-sm text-sm" : "",
                    ].filter(Boolean).join(" ") || undefined}
                >
                    {inner}
                </span>
            );
        }

        // ── Link ───────────────────────────────────────────────
        if (node.type === "link") {
            return (
                <a
                    key={Math.random()}
                    href={node.url}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-saansBold transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {node.children?.map(renderNode)}
                </a>
            );
        }

        // ── List item ──────────────────────────────────────────
        if (["list-item", "list_item", "listItem"].includes(node.type)) {
            return (
                <li key={index} className="ml-6 mb-2 marker:text-[#0475B3]">
                    {node.children?.map(renderNode)}
                </li>
            );
        }

        // ── Block elements ─────────────────────────────────────
        if (node.children && Array.isArray(node.children)) {

            // ── Empty paragraph → visible spacer ──────────────
            const isEmpty = node.type === "paragraph" &&
                node.children.every((c) => c.type === "text" && (c.text ?? "").trim() === "");

            if (isEmpty) {
                // Each empty paragraph = one blank line of breathing room
                return <div key={index} className="h-4" aria-hidden="true" />;
            }

            const children = node.children.map(renderNode);

            const hasBlockChild = node.children.some((c) =>
                c?.type && [
                    "paragraph","heading","heading-one","heading-two","heading-three",
                    "h1","h2","h3","h4","h5","h6",
                    "ul","ol","list-item","blockquote",
                ].includes(c.type)
            );

            let tagName = "div";
            switch (node.type) {
                case "paragraph":
                    tagName = disableParagraph ? "div" : (hasBlockChild ? "div" : "p");
                    break;
                case "h1": case "heading-one":   case "heading1": tagName = "h1"; break;
                case "h2": case "heading-two":   case "heading2": tagName = "h2"; break;
                case "h3": case "heading-three": case "heading3": tagName = "h3"; break;
                case "h4": case "heading-four":  case "heading4": tagName = "h4"; break;
                case "h5": case "heading-five":  case "heading5": tagName = "h5"; break;
                case "h6": case "heading-six":   case "heading6": tagName = "h6"; break;
                case "heading":
                    tagName = node.level ? `h${Math.min(Math.max(node.level, 1), 6)}` : "h3";
                    break;
                case "ul": case "bullet-list": case "unordered-list": tagName = "ul"; break;
                case "ol": case "ordered-list":                        tagName = "ol"; break;
                case "blockquote": tagName = "blockquote"; break;
                case "code": case "pre": case "code-block":
                    tagName = node.type === "pre" ? "pre" : "code";
                    break;
                default: tagName = "div";
            }

            const blockClass = {
                p:          disableParagraph
                                ? "mb-3 leading-relaxed inline-block"
                                : "mb-3 leading-relaxed",
                h1:         "text-4xl font-saansBold mb-6 mt-2",
                h2:         "text-3xl font-saansBold mb-5 mt-2",
                h3:         "text-2xl font-saansBold mb-4 mt-2",
                h4:         "text-xl  font-saansBold mb-3 mt-1",
                h5:         "text-lg  font-saansBold mb-3 mt-1",
                h6:         "text-base font-saansBold mb-2",
                ul:         "list-disc   ml-6 mb-4 space-y-1",
                ol:         "list-decimal ml-6 mb-4 space-y-1",
                blockquote: "border-l-4 border-slate-300 pl-4 italic text-slate-600 mb-4",
                code:       "bg-slate-100 p-3 rounded text-sm font-mono overflow-x-auto mb-4",
                pre:        "bg-slate-100 p-3 rounded text-sm font-mono overflow-x-auto mb-4 whitespace-pre-wrap",
            }[tagName] || "mb-2";

            return React.createElement(
                tagName,
                {
                    key: index,
                    className: `${blockClass} ${className}`.trim(),
                },
                children
            );
        }

        return null;
    };

    if (Array.isArray(content)) {
        return React.createElement(
            tag,
            {
                className: disableParagraph
                    ? `${className} inline-block`.trim()
                    : className,
                style,
            },
            content.map((node, i) => renderNode(node, i))
        );
    }

    return renderNode(content, 0) || (
        <span className={className} style={style}>
            {content}
        </span>
    );
};

export default RichTextRenderer;


// "use client";
// import React from "react";

// const RichTextRenderer = ({ content, className = "", tag = "div", style = {}, forceFont = false, disableParagraph = false }) => {
//     const renderNode = (node) => {
//         if (!node) return null;
//         if (typeof node === "string") return node;

//         // Text node with formatting
//         if (node.type === "text") {
//             const isBold = node.bold === true || node.marks?.some((m) => m.type === "bold");
//             const isItalic = node.italic === true || node.marks?.some((m) => m.type === "italic");
//             const isUnderlined = node.underline === true || node.marks?.some((m) => m.type === "underline");
//             const isStrikethrough = node.strikethrough === true || node.deleted === true || node.marks?.some((m) => ["strikethrough", "deleted"].includes(m.type));
//             const isCode = node.code === true || node.marks?.some((m) => m.type === "code");

//             return (
//                 <span
//                     key={Math.random()}
//                     style={{
//                         fontWeight: isBold ? "bold" : "normal",
//                         fontStyle: isItalic ? "italic" : "normal",
//                         textDecoration: isStrikethrough ? "line-through" : isUnderlined ? "underline" : "none",
//                         ...style
//                     }}
//                     className={`${isBold ? "font-saansBold" : ""} ${forceFont ? "font-saansBold" : ""} ${isItalic ? "italic" : ""} ${isCode ? "font-mono bg-slate-100 px-1 rounded-sm" : ""}`.trim()}
//                 >
//                     {node.text || ""}
//                 </span>
//             );
//         }

//         // Link node
//         if (node.type === "link") {
//             return (
//                 <a
//                     key={Math.random()}
//                     href={node.url}
//                     className="text-blue-600 hover:text-blue-800 hover:underline font-saansBold transition-colors"
//                     target="_blank"
//                     rel="noopener noreferrer"
//                 >
//                     {node.children?.map(renderNode)}
//                 </a>
//             );
//         }

//         // List items
//         if (node.type === "list-item" || node.type === "list_item" || node.type === "listItem") {
//             return (
//                 <li key={Math.random()} className="ml-6 mb-2 list-disc marker:text-[#0475B3]">
//                     {node.children?.map(renderNode)}
//                 </li>
//             );
//         }

//         // Block elements
//         if (node.children && Array.isArray(node.children)) {
//             const children = node.children.map(renderNode);
//             let tagName = "div";

//             const hasBlockChild = node.children.some(c => {
//                 return c && c.type && ["paragraph", "heading", "heading-one", "heading-two", "heading-three", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "list-item", "blockquote"].includes(c.type);
//             });

//             switch (node.type) {
//                 case "paragraph":
//                     tagName = disableParagraph ? "div" : (hasBlockChild ? "div" : "p");
//                     break;
//                 case "h1":
//                 case "heading-one":
//                 case "heading1":
//                     tagName = "h1";
//                     break;
//                 case "h2":
//                 case "heading-two":
//                 case "heading2":
//                     tagName = "h2";
//                     break;
//                 case "h3":
//                 case "heading-three":
//                 case "heading3":
//                     tagName = "h3";
//                     break;
//                 case "h4":
//                 case "heading-four":
//                 case "heading4":
//                     tagName = "h4";
//                     break;
//                 case "h5":
//                 case "heading-five":
//                 case "heading5":
//                     tagName = "h5";
//                     break;
//                 case "h6":
//                 case "heading-six":
//                 case "heading6":
//                     tagName = "h6";
//                     break;
//                 case "heading":
//                     tagName = node.level ? `h${Math.min(Math.max(node.level, 1), 6)}` : "h3";
//                     break;
//                 case "ul":
//                 case "bullet-list":
//                 case "unordered-list":
//                     tagName = "ul";
//                     break;
//                 case "ol":
//                 case "ordered-list":
//                     tagName = "ol";
//                     break;
//                 case "blockquote":
//                     tagName = "blockquote";
//                     break;
//                 case "code":
//                 case "pre":
//                 case "code-block":
//                     tagName = node.type === "pre" ? "pre" : "code";
//                     break;
//                 default:
//                     tagName = "div";
//             }

//             const blockClass = {
//                 paragraph: disableParagraph ? "mb-4 leading-relaxed inline-block" : "mb-4 leading-relaxed",
//                 h1: "text-4xl font-saansBold mb-6",
//                 h2: "text-3xl font-saansBold mb-5",
//                 h3: "text-2xl font-saansBold mb-4",
//                 h4: "text-xl font-saansBold mb-3",
//                 h5: "text-lg font-saansBold mb-3",
//                 h6: "text-base font-saansBold mb-2",
//                 ul: "list-disc ml-6 mb-4 space-y-2",
//                 ol: "list-decimal ml-6 mb-4 space-y-2",
//                 blockquote: "border-l-4 border-slate-300 pl-4 italic text-slate-700 mb-4",
//                 code: "bg-slate-100 p-3 rounded text-sm font-mono overflow-x-auto"
//             }[tagName] || "";

//             return React.createElement(
//                 tagName,
//                 {
//                     key: Math.random(),
//                     className: `${blockClass} ${className}`.trim()
//                 },
//                 children
//             );
//         }

//         return null;
//     };

//     // Handle array or single node
//     if (Array.isArray(content)) {
//         return React.createElement(
//             tag,
//             { className: disableParagraph ? `${className} inline-block` : className, style },
//             content.map(renderNode)
//         );
//     }

//     return renderNode(content) || <span className={className} style={style}>{content}</span>;
// };

// export default RichTextRenderer;
